import { CaretDown, Check, Copy } from "@phosphor-icons/react";
import {
  Button,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuProvider,
} from "@replicate/ui";
import { useMutation } from "@tanstack/react-query";
import copy from "copy-to-clipboard";
import { useState } from "react";
import { useSpinDelay } from "spin-delay";

import { AnimatePresence, motion } from "framer-motion";
import type { Model } from "../../types";

async function fetchAndCopyPageContent(model: Model): Promise<string> {
  const res = await fetch(`/${model.owner}/${model.name}/llms.txt`, {
    method: "GET",
    headers: {
      "Content-Type": "text/plain",
    },
    cache: "no-store",
  });
  if (!res.ok) {
    throw new Error("Failed to fetch the page content");
  }
  const text = await res.text();
  try {
    await navigator.clipboard.writeText(text);
  } catch (e) {
    copy(text);
  }
  return text;
}

export default function ModelDetailButton({ model }: { model: Model }) {
  const [copied, setCopied] = useState(false);
  const { mutate, status } = useMutation({
    mutationFn: () => {
      return fetchAndCopyPageContent(model);
    },
    onSuccess(data, variables, context) {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    },
  });

  const query = `Show me how to run this model on Replicate https://replicate.com/${model.owner}/${model.name}/llms.txt`;

  const chatGptUrl = `https://chatgpt.com/?hints=search&q=${encodeURIComponent(
    query
  )}`;

  const claudeUrl = `https://claude.ai/new?q=${encodeURIComponent(query)}`;

  const handleCopy = () => {
    mutate();
  };

  const isCopyingToClipboard = status === "pending";

  const showLoadingSpinner = useSpinDelay(isCopyingToClipboard);

  return (
    <div className="flex -space-x-px">
      <Button
        loading={showLoadingSpinner}
        onClick={handleCopy}
        className="w-full justify-start truncate relative"
        startIcon={
          <AnimatePresence initial={false} mode="wait">
            <motion.div
              key={`copy-${copied}`}
              exit={{ opacity: 0 }}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.2 }}
            >
              {copied ? (
                <Check weight="bold" className="text-r8-green-10" />
              ) : (
                <Copy />
              )}
            </motion.div>
          </AnimatePresence>
        }
        variant="outlined"
        size="sm"
        title="Copy page content"
      >
        Copy
      </Button>
      <MenuProvider placement="bottom-end">
        <MenuButton
          render={<IconButton variant="outlined" size="sm" className="p-4" />}
        >
          <CaretDown weight="bold" />
        </MenuButton>
        <Menu gutter={0}>
          <MenuItem
            className="text-r8-sm"
            render={
              // biome-ignore lint/a11y/useAnchorContent: content injected
              <a
                href={`/${model.owner}/${model.name}/llms.txt`}
                className="no-underline no-focus"
                target="_blank"
                rel="noreferrer"
              />
            }
          >
            View as Markdown
          </MenuItem>
          <MenuItem
            className="text-r8-sm"
            render={
              // biome-ignore lint/a11y/useAnchorContent: content injected
              <a
                className="no-underline no-focus"
                target="_blank"
                href={chatGptUrl}
                rel="noreferrer"
              />
            }
          >
            Open in ChatGPT
          </MenuItem>
          <MenuItem
            className="text-r8-sm"
            render={
              // biome-ignore lint/a11y/useAnchorContent: content injected
              <a
                className="no-underline no-focus"
                target="_blank"
                href={claudeUrl}
                rel="noreferrer"
              />
            }
          >
            Open in Claude
          </MenuItem>
        </Menu>
      </MenuProvider>
    </div>
  );
}
